import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Layout from "../components/Layout";
import {
  ApiError,
  endpoints,
  type Country,
  type Locale,
  type Tenant,
} from "../services/api";
import styles from "./PlanPage.module.css";

const CSI_LM_URL = "https://upg.seleco.pro";

function StatusBadge({ status }: { status: string | null }) {
  const klass =
    status === "ACTIVE" || status === "active"
      ? styles.badgeOk
      : status
        ? styles.badgeWarn
        : styles.badgeMuted;
  return <span className={`${styles.badge} ${klass}`}>{status ?? "—"}</span>;
}

function formatDate(iso: string | null, locale: Locale): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" });
}

export default function PlanPage() {
  const { t, i18n } = useTranslation();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [country, setCountry] = useState<Country>("PL");
  const [locale, setLocale] = useState<Locale>("pl-PL");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    endpoints
      .me()
      .then((me) => {
        if (cancelled) return;
        setTenant(me);
        setCountry(me.country);
        setLocale(me.locale);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : "Błąd ładowania konta");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const dirty = useMemo(
    () => tenant !== null && (tenant.country !== country || tenant.locale !== locale),
    [tenant, country, locale],
  );

  async function handleSave() {
    if (!dirty) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await endpoints.updateRegion(country, locale);
      setTenant(updated);
      const i18nLang = locale === "pl-PL" ? "pl" : "en";
      if (i18n.language !== i18nLang) {
        await i18n.changeLanguage(i18nLang);
      }
      setSavedAt(Date.now());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Błąd zapisu");
    } finally {
      setSaving(false);
    }
  }

  // Po 3s znikamy „Zapisano"
  useEffect(() => {
    if (savedAt === null) return;
    const handle = window.setTimeout(() => setSavedAt(null), 3000);
    return () => window.clearTimeout(handle);
  }, [savedAt]);

  return (
    <Layout>
      <header className="page-header">
        <h1>{t("plan.title")}</h1>
      </header>

      {loading && (
        <section className="card">
          <p>Ładowanie…</p>
        </section>
      )}

      {error && (
        <section className="card empty-state">
          <p style={{ color: "var(--color-danger)" }}>{error}</p>
        </section>
      )}

      {!loading && tenant && (
        <>
          <section className="card">
            <h2 className="card__title">{t("plan.regionSection")}</h2>
            <div className={styles.formGrid}>
              <fieldset className={styles.fieldset}>
                <legend>{t("plan.country")}</legend>
                <label className={styles.radio}>
                  <input
                    type="radio"
                    name="country"
                    value="PL"
                    checked={country === "PL"}
                    onChange={() => setCountry("PL")}
                  />
                  <span>{t("plan.countryPL")}</span>
                </label>
                <label className={styles.radio}>
                  <input
                    type="radio"
                    name="country"
                    value="UK"
                    checked={country === "UK"}
                    onChange={() => setCountry("UK")}
                  />
                  <span>{t("plan.countryUK")}</span>
                </label>
              </fieldset>

              <label className={styles.field}>
                <span>{t("plan.language")}</span>
                <select
                  value={locale}
                  onChange={(e) => setLocale(e.target.value as Locale)}
                >
                  <option value="pl-PL">{t("plan.languagePL")}</option>
                  <option value="en-GB">{t("plan.languageEN")}</option>
                </select>
              </label>
            </div>

            <div className={styles.actions}>
              <button
                className="btn btn--primary"
                disabled={!dirty || saving}
                onClick={handleSave}
              >
                {saving ? "…" : t("plan.save")}
              </button>
              {savedAt !== null && <span className={styles.savedNote}>✓ {t("plan.saved")}</span>}
            </div>
          </section>

          <section className="card">
            <h2 className="card__title">{t("plan.subscriptionSection")}</h2>
            <dl className={styles.dl}>
              <dt>{t("plan.currentPlan")}</dt>
              <dd>
                <strong>{tenant.plan}</strong> · {tenant.max_stores}{" "}
                {tenant.max_stores === 1 ? "sklep" : "sklepów"}
              </dd>
              <dt>{t("plan.monthlyFee")}</dt>
              <dd>{tenant.monthly_fee} {country === "PL" ? "zł" : "GBP"}/m</dd>
              <dt>{t("plan.license")}</dt>
              <dd className={styles.mono}>{tenant.license_status ? "BB-•••" : "—"}</dd>
              <dt>{t("plan.licenseStatus")}</dt>
              <dd><StatusBadge status={tenant.license_status} /></dd>
              <dt>{t("plan.licenseValidUntil")}</dt>
              <dd>{formatDate(tenant.license_expires_at, locale)}</dd>
            </dl>

            <h3 className={styles.subheading}>{t("plan.pricing")}</h3>
            <ul className={styles.pricing}>
              <li>{t("plan.pricingStarter")}</li>
              <li>{t("plan.pricingGrowth")}</li>
              <li>{t("plan.pricingPro")}</li>
              <li>{t("plan.pricingEnterprise")}</li>
            </ul>

            <a
              href={CSI_LM_URL}
              target="_blank"
              rel="noreferrer noopener"
              className="btn btn--secondary"
            >
              {t("plan.manageInCSI")} ↗
            </a>
          </section>

          <section className="card">
            <h2 className="card__title">{t("plan.securitySection")}</h2>
            <dl className={styles.dl}>
              <dt>{t("plan.email")}</dt>
              <dd>{tenant.email}</dd>
            </dl>
            <button className="btn btn--secondary" disabled title={t("plan.changePasswordSoon")}>
              {t("plan.changePassword")}
            </button>
            <p className={styles.muted}>{t("plan.changePasswordSoon")}</p>
          </section>
        </>
      )}
    </Layout>
  );
}
